import { useState } from "react";
import { Lock, X, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";

export default function ChangePasswordModal({ open, onClose }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!open) return null;

  const inputCls =
    "w-full px-4 py-3 rounded-lg bg-[#050505] border border-white/10 text-white placeholder-zinc-600 focus:outline-none focus:border-electric focus:ring-1 focus:ring-electric/30 transition";

  const reset = () => {
    setCurrent("");
    setNext("");
    setConfirm("");
  };

  const handleClose = () => {
    if (submitting) return;
    reset();
    onClose();
  };

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) {
      toast.error("New password must be at least 8 characters.");
      return;
    }
    if (next !== confirm) {
      toast.error("New password and confirmation do not match.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      toast.success("Password updated successfully.");
      reset();
      onClose();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Could not update password."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center px-5 bg-black/70 backdrop-blur-sm"
      onClick={handleClose}
      data-testid="change-password-modal"
    >
      <div
        className="relative w-full max-w-md rounded-2xl bg-[#0a0a0a] border border-white/5 p-7"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={handleClose}
          aria-label="Close"
          className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-zinc-400 hover:text-white transition"
          data-testid="cpm-close"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="mb-6">
          <div className="text-[11px] uppercase tracking-[0.4em] text-electric font-semibold mb-2">
            Account
          </div>
          <h2 className="font-display font-black text-2xl tracking-tighter">Change password</h2>
          <p className="text-sm text-zinc-400 mt-1">
            Use a strong password of at least 8 characters.
          </p>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <Field label="Current password" icon={<Lock className="w-3.5 h-3.5" />}>
            <input
              type="password"
              autoComplete="current-password"
              required
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              className={inputCls}
              data-testid="cpm-current"
            />
          </Field>
          <Field label="New password" icon={<Lock className="w-3.5 h-3.5" />}>
            <input
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              value={next}
              onChange={(e) => setNext(e.target.value)}
              className={inputCls}
              placeholder="At least 8 characters"
              data-testid="cpm-new"
            />
          </Field>
          <Field label="Confirm new password" icon={<Lock className="w-3.5 h-3.5" />}>
            <input
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className={inputCls}
              data-testid="cpm-confirm"
            />
          </Field>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={handleClose}
              disabled={submitting}
              className="flex-1 inline-flex items-center justify-center gap-2 border border-white/15 text-white px-5 py-3 rounded-full font-semibold tracking-wide hover:border-electric hover:text-electric transition disabled:opacity-50"
              data-testid="cpm-cancel"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 inline-flex items-center justify-center gap-2 bg-electric text-black px-5 py-3 rounded-full font-bold tracking-wide hover:bg-[#4ade80] transition disabled:opacity-60"
              data-testid="cpm-submit"
            >
              <CheckCircle2 className="w-4 h-4" />
              {submitting ? "Updating..." : "Update"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, icon, children }) {
  return (
    <div>
      <label className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.25em] text-zinc-400 font-bold mb-2">
        {icon}
        {label}
      </label>
      {children}
    </div>
  );
}
